import angular from "angular";

import { AccountInIndex, Ajax, Avatar, Dates, Invoice, InvoiceListItem, Invoices, InvoicesLogic, InvoiceStatus, InvoiceType, Numeric, PaymentChannel, SystemRole, ODataQueryOptions,Translations, Promises, BicHelper, Arrays } from "@salaxy/core";
import { Barcodes, QrCodes } from "@salaxy/reports";
import { InvoicesService, SessionService, UiHelpers } from "../../services";
import { InvoiceCrudController } from "./InvoiceCrudController";


type GroupedItem = {
  /** Group identifier / key */
  id: string,
  /** Owner (employer) info */
  ownerInfo: AccountInIndex,
  /** Invoice type */
  invoiceType: InvoiceType,
  /** Status of the payments */
  status: InvoiceStatus,
  /** Due date of the payments */
  dueDate: string,
  /** Total payment for the group */
  payment: number,
  /** Payment channel for the group. */
  channel: string,
  /** Key data of the invoices */
  invoices: any[],
}
/**
 * Extends the InvoiceCrudController to provide
 * SEPA payment management functionality.
 */
export class InvoiceSepaController extends InvoiceCrudController {

  /**
   * For NG-dependency injection
   */
  public static $inject = [
    "Invoices",
    "UiHelpers",
    "$location",
    "$routeParams",
    "InvoicesService",
    "AjaxNg1",
    "SessionService",
    "$http",
    "$timeout",
    "$sce"
  ];

    /** URL for the OData list service, null for preventing automatic initial load */
    public get odataServiceUrl(): string {
      return null;
    }

  /** List items grouped by owner, invoiceType, status and dueDate */
  public groupedItems: Array<GroupedItem>;

  /** Year for invoices query */
  public queryYear = Dates.getToday();

  /** Status filters for invoices */
  public invoiceEntityTypes = [
    { label: Translations.get("SALAXY.NG1.InvoicesComponent.all"), value: "all" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.net.label"), value: "net" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.pension.label"), value: "pension" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.tax.label"), value: "tax" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.unemployment.label"), value: "unemployment" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.union.label"), value: "union" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.payroll.label"), value: "payroll" },
  ];

  /** Invoice entity type used in filtering */
  public invoiceEntityType = this.invoiceEntityTypes.find((x) => x.value === "all");

  /** The current status filter. "waitingConfirmation" is the default. */
  public statusFilter = this.statusFilters.find((x) => x.value === "waitingConfirmation");

  /** Default sepa channel */
  public sepaChannel:PaymentChannel;

  private _allChannels: Avatar[] = null;

  private qrCodesCache: { [key: string]: string } = {};

  constructor(
    invoicesFullApi: Invoices,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private invService: InvoicesService,
    private sepaAjax: Ajax,
    private session: SessionService,
    private $http: angular.IHttpService,
    private $timeout: angular.ITimeoutService,
    private $sce: angular.ISCEService
  ) {
    // Dependency injection
    super(invoicesFullApi, uiHelpers, $location, $routeParams, invService, sepaAjax);
  }

  /** Intitializes the controller */
  public $onInit() {
    this.sepaChannel ??= this.invService.defaultSepaChannel;
    super.$onInit();
    this.loadData();
  }

  /**
   * Loads the data for the controller.
   */
  public loadData() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    this.loadAllInvoices().then((items) => {
      this.updateGroupedItems(items);
      loading.dismiss();
      this.$timeout();
    });
  }

  /**
   * Loads all invoices from the server.
   * @returns Promise of all invoices
   */
  public async loadAllInvoices(): Promise<InvoiceListItem[]> {
    const items: any[] = [];
    const loadItems = (nextPageLink: string = null): Promise<string> => {
      let options: ODataQueryOptions = {$filter: `data/channel eq '${this.sepaChannel}' and endAt ge ${Dates.getYear(this.queryYear)}-01-01 and endAt le ${Dates.getYear(this.queryYear)}-12-31`, $orderby: "id"};
      const queryString = nextPageLink?.indexOf("?") ?? -1;
      if (queryString >= 0) {
        const searchParams = new URLSearchParams(nextPageLink.substr(queryString));
        if (searchParams.get("$skip")) {
          options = { $skip:  Number(searchParams.get("$skip")), $filter: `data/channel eq '${this.sepaChannel}' and endAt ge ${Dates.getYear(this.queryYear)}-01-01 and endAt le ${Dates.getYear(this.queryYear)}-12-31`, $orderby: "id"};
        }
      }
      return this.invoicesApi.getOData(options).then((data) => {
        items.push(...data.items);
        return data.nextPageLink ? loadItems(data.nextPageLink) : null;
      });
    };

    return loadItems().then(() => {
      return Arrays.distinct(items, (item) => item.id).sort((a, b) => a.endAt > b.endAt ? -1 : 1);
    });

  }

  /**
   * Shows a dialog for a group of invoices.
   * @param group Group of invoices as defined in groupedItems.
   */
  public showGroupDialog(group: any) {
    this.uiHelpers.openEditDialog("salaxy-components/modals/invoices/invoice-group-dialog.html", group, { parent: this }, "xl");
  }

  /**
   * Shows a dialog for a single invoice.
   * @param item Invoice item.
   */
  public showInvoiceDialog(item: InvoiceListItem) {
    this.uiHelpers.openEditDialog("salaxy-components/modals/invoices/invoice-dialog.html", item, { parent: this }, "xl");
  }

  /** Filter function for angularjs filter used in template */
  public invoiceFilter = (value: InvoiceListItem | any) => {
    if(this.statusFilter.value === "all" && this.invoiceEntityType.value === "all") {
      return true;
    }

    if(this.invoiceEntityType.value !== "all" && value.invoiceType !== this.invoiceEntityType.value) {
      return false;
    }

    const statuses = {
      waitingConfirmation: [InvoiceStatus.Unread, InvoiceStatus.Read, InvoiceStatus.WaitingConfirmation],
      paid: [InvoiceStatus.Paid, InvoiceStatus.PaymentStarted, InvoiceStatus.Canceled],
      forecast: [InvoiceStatus.Forecast, InvoiceStatus.Preview]
    };

    if (statuses[this.statusFilter.value]?.indexOf(value.status) === -1) {
      return false;
    }

    return true;
  };

  /** Gets the grouped items, creating them if necessary. */
  public getGroupedItems() {
    return this.groupedItems;
  }

  /**
   * Checks if a group contains any duplicate invoices based on their IDs.
   * Iterates through each invoice in the group and checks if there are multiple invoices
   * with the same ID across all grouped items.
   * @param group - The group of invoices to check for duplicates.
   * @returns {boolean} - Returns true if any duplicate invoice IDs are found, otherwise false.
   */
  public groupHasDuplicateInvoices(group: GroupedItem): boolean {
    const allInvoices = this.getGroupedItems().flatMap(g => g.invoices);
    for (const groupInvoice of group.invoices) {
      const invoicesWithSameId = allInvoices.filter(invoice => invoice.id === groupInvoice.id);
      if (invoicesWithSameId.length > 1) {
        console.log("duplicate invoices", invoicesWithSameId);
        return true;
      }
    }
    return false;
  }

  /** Updates the grouped items based on the ODataQueryController.  */
  public updateGroupedItems(items: InvoiceListItem[]) {
    this.groupedItems = [];
    items.forEach((l) => {
      const id = l.ownerId + l.entityType + l.status + l.data.dueDate + l.data.channel;
      let group = this.groupedItems.find((x) => x.id === id);
      if (!group) {
        group = {
          id: id,
          ownerInfo: l.ownerInfo,
          invoiceType: l.entityType as InvoiceType,
          status: l.status,
          dueDate: l.data.dueDate,
          payment: 0,
          channel: l.data.channel,
          invoices: [],
        };
        this.groupedItems.push(group);
      }
      group.payment += l.payment;
      group.invoices.push(l);
    });
  }

  /** Data reader for one item. */
  public getGroupDataReader = (
    group:
      {
        /** invoices */
        invoices: InvoiceListItem[],
      },
    status: InvoiceStatus) => {
    return {
      /** Function to read data into given array */
      read: (arr: Invoice[], message: string): Promise<void> => {
        const showConfirmation = (): Promise<boolean> => {
          const warning = this.getEprWarning(group.invoices, status);
          if (warning) {
            return this.uiHelpers.showConfirm("Huomioi palkkatietoilmoitukset!", warning);
          }
          return Promise.resolve(true);
        };
        return showConfirmation().then((result) => {
          if (result) {
            (group as any)["isReading" + (status || "")] = true;
            const promises = group.invoices.map((x) => () => this.getInvoice(x, message, status));
            return Promises.sequence(promises).then( (invoices) => {
              arr.push(...invoices.filter(i => !!i));
              (group as any)["isReading" + (status || "")] = false;
              if (status) {
                (group as any).status = status;
              }
              return;
            });
          } else {
            return;
          }
        });
      },
      getSepaData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<string> => this.getSepaData(arr),
      getFinvoiceData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<Blob[]> => this.getFinvoiceData(arr),
      getPdfData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<Blob[]> => this.getPdfData(arr),
      /** Optional label for data export */
      exportLabel: null,
      /** Indicates if the export is not possible */
      disabled: () => false,
    };
  };

  /** Data reader for one item. */
  public getGroupItemDataReader = (item: InvoiceListItem, status: InvoiceStatus) => {
    return {
      /** Function to read data into given array */
      read: (arr: Invoice[], message: string): Promise<void> => {
        const showConfirmation = (): Promise<boolean> => {
          const warning = this.getEprWarning([item], status);
          if (warning) {
            return this.uiHelpers.showConfirm("Huomioi palkkatietoilmoitukset!", warning);
          }
          return Promise.resolve(true);
        };
        return showConfirmation().then((result) => {
          if (result) {
            (item as any)["isReading" + (status || "")] = true;
            return this.getInvoice(item, message, status).then((data) => {
              if (data) {
                arr.push(data);
              }
              (item as any)["isReading" + (status || "")] = false;
              return;
            });
          } else {
            return;
          }
        });
      },
      getSepaData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<string> => this.getSepaData(arr),
      getFinvoiceData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<Blob[]> => this.getFinvoiceData(arr),
      getPdfData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<Blob[]> => this.getPdfData(arr),
      /** Optional label for data export */
      exportLabel: null,
      /** Indicates if the export is not possible */
      disabled: () => false,
    };
  };

  /**
   * Returns PDF url for a single invoice.
   * @param item Invoice list item.
   */
  public getPdfUrl(item: InvoiceListItem): string {
    if (!(item as any).pdfUrl) {
        (item as any).pdfUrl = (this.api as Invoices).getContentUrl(item.id, false);
    }
    return (item as any).pdfUrl;
  }

  /**
   * Retrieves the QR code for the specified invoice item.
   * If the QR code is not already cached, it generates the QR code and caches it for future use.
   * @param item - The invoice item for which to retrieve the QR code.
   * @returns The QR code string.
   */
  public getQrCode(item: InvoiceListItem): string {
    if(!this.qrCodesCache[item.id]) {
      this.qrCodesCache[item.id] = "loading";
      this.generateQrCodeSvgForInvoice(item).then((qrCode) => {
        if(!qrCode) {
          this.qrCodesCache[item.id] = "error";
          this.$timeout();
          return this.qrCodesCache[item.id];
        } else {
          this.qrCodesCache[item.id] = qrCode;
          this.$timeout();
          return this.$sce.trustAsHtml(this.qrCodesCache[item.id]);
        }
      });
    }
    if(this.qrCodesCache[item.id] === "error") {
      return this.qrCodesCache[item.id];
    }
    return this.$sce.trustAsHtml(this.qrCodesCache[item.id]);
  }

  /**
   * Calculates barcode value for the invoice item.
   * @param item Invoice list item.
   */
  public getBarcodeValue(item: InvoiceListItem): string {
    return Barcodes.getValue(
      item.otherPartyInfo.ibanNumber || "",
      (item as any).reference || "",
      Dates.asDate(item.data.dueDate),
      Numeric.round(item.payment * 100, 0));
  }

  /**
   * Retrieves the barcode for the invoice recipient's IBAN.
   * If the IBAN does not start with "fi" or is not provided, an empty string is returned.
   * The barcode is generated using the Barcodes.createBarcode function.
   * @returns The barcode as a string, wrapped in an HTML trust object.
   */
  public getBarcode(item: InvoiceListItem): string {
    if (!item.otherPartyInfo.ibanNumber || !item.otherPartyInfo.ibanNumber.toLowerCase().trim().startsWith("fi")) {
      return "";
    }
    const getDocumentAndSvgNode = () => {
      return {document, svgNode: document.createElementNS("http://www.w3.org/2000/svg", "svg")};
    };

    const result = Barcodes.createBarcode(
      getDocumentAndSvgNode,
      item.otherPartyInfo.ibanNumber || "",
      (item as any).reference || "",
      Dates.asDate(item.data.dueDate),
      Numeric.round(item.payment * 100, 0),
      {
        width: 1.8,
        height: 32,
        displayValue: true,
        fontSize: 15
      }
    );
    if(!result) {
      return "";
    }
    return this.$sce.trustAsHtml(new XMLSerializer().serializeToString(result as any));
  }

  /** Hides bar code for forecast and preview invoice statuses*/
  public hideBarCode(item: InvoiceListItem): boolean {
    if(item.status === InvoiceStatus.Forecast || item.status === InvoiceStatus.Preview) {
      return true;
    }
    return false;
  }

  /**
   * Copies the barcode to clipboard.
   * @param item Invoice list item.
   */
  public copyBarcode(item: InvoiceListItem) {
    (navigator as any).clipboard.writeText(this.getBarcodeValue(item));
  }

  /** Gets and avatar image for a payment channel */
  public getChannelAvatar(channel: PaymentChannel) {
    return InvoicesLogic.getChannelAvatar(channel);
  }

  /** Update status of invoices in group using the group data reader. */
  public updateInvoiceGroupStatus(
    group:
      {
        /** invoices */
        invoices: InvoiceListItem[],
      },
    status: InvoiceStatus) {
    const loading = this.uiHelpers.showLoading("Odota...");
    const reader = this.getGroupDataReader(group, status);
    reader.read([], null).then(() => {
      loading.dismiss();
    });
  }

  /** Update status of single invoice using the group data reader. */
  public updateInvoiceGroupItemStatus(item: InvoiceListItem, status: InvoiceStatus) {
    const loading = this.uiHelpers.showLoading("Odota...");
    const reader = this.getGroupItemDataReader(item, status);
    reader.read([], null).then(() => {
      loading.dismiss();
    });
  }

  /**
   * Gets all channels as avatars for drop-down.
   */
  public getAllChannels(): Avatar[] {
    if (this.session.getSession() && this._allChannels === null) {
      const settings = this.session.getSession().settings;
      let available = (settings.paymentChannels || []);
      if (settings.defaultPaymentChannel) {
        available = available.filter((x) => x !== settings.defaultPaymentChannel);
        available.unshift(settings.defaultPaymentChannel);
      }
      if (available.indexOf(PaymentChannel.ZeroPayment) < 0) {
        available.push(PaymentChannel.ZeroPayment);
      }
      this._allChannels = available
        .map((x) => InvoicesLogic.getChannelAvatar(x));
    }
    return this._allChannels;
  }

  /**
   * Summs payment fields of InvoiceListImte array
   * @returns summed values
   */
  public sumInvoicePayments(invoices: Array<InvoiceListItem>) {
    let sum = 0;
    invoices.forEach(invoice => {
      sum += invoice.payment;
    });
    return sum;
  }

  private getInvoice(item: InvoiceListItem, message: any, status: InvoiceStatus = null): Promise<Invoice> {

    const updateInvoiceStatus = (invoice: Invoice, status: InvoiceStatus): Promise<Invoice> => {
      if (invoice.header.status === status) {
        return Promise.resolve(invoice);
      }
      return (this.api as Invoices).requestStatusUpdate(item.id, status);
    };

    return this.api.getSingle(item.id)
      .then((readInvoice: Invoice) => {
        if (status) {
          return updateInvoiceStatus(readInvoice, status).then((updatedInvoice) => {
            item.status = updatedInvoice.header.status;
            return updatedInvoice;
          });
        } else {
          return readInvoice;
        }
      });
  }

  private async getPdfData(invoices: Invoice[]): Promise<Blob[]> {
    const blobs: Blob[] = [];
    const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    for (const [index, invoice] of invoices.entries()) {
      const blob = await this.download("/v03/api/invoices/content", invoice, "blob");
      loader.setText(`${index}/${invoices.length}`);
      blobs.push(blob);
    }
    loader.dismiss();
    return blobs;
  }

  private async getFinvoiceData(invoices: Invoice[]): Promise<Blob[]> {
    const blobs: Blob[] = [];
    const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    for (const [index, invoice] of invoices.entries()) {
      const blob = await this.download("/v03/api/invoices/finvoice", invoice, "blob");
      loader.setText(`${index}/${invoices.length}`);
      blobs.push(blob);
    }
    loader.dismiss();
    return blobs;
  }

  private getSepaData(invoices: Invoice[], payerIbanNumber: string = null): Promise<string> {
    const method = `/v03/api/invoices/sepa${payerIbanNumber ? "?payerIbanNumber=" + encodeURIComponent(payerIbanNumber) : ""}`;
    return this.download(method, invoices, "text");
  }
  private download(method: string, data: any, responseType: string): Promise<any> {

    const request: any = {}; // angular.IRequestConfig

    const token: string = this.sepaAjax.getCurrentToken();
    if (token) {
      request.headers = { Authorization: "Bearer " + token };
    }

    request.url = this.sepaAjax.getServerAddress() + method;
    request.method = "POST";
    request.data = data;
    request.responseType = responseType;
    request.withCredentials = (token) ? false : this.sepaAjax.useCredentials;

    return (this.$http(request).then(
      (response) => response.data,
      (error: any) => {
        return null;
      }) as any
    );
  }


  private getEprWarning(items: InvoiceListItem[], status: InvoiceStatus): string {
    if (status === InvoiceStatus.Canceled) {
      for (const item of items) {
        if (item.status === InvoiceStatus.Paid &&
          item.entityType == InvoiceType.Net &&
          this.isDelayed(item.logicalDate)) {
          return "Palkanmaksusta tehdyt tulorekisteri-ilmoitukset perutaan automaattisesti.\n\nJos maksat palkan uudestaan samalle palkkapäivälle, ota huomioon, että uudesta palkkatietoilmoituksesta saattaa seurata myöhästymismaksu. Haluatko varmasti perua maksut?";
        }
      }
    } else if (status === InvoiceStatus.Paid) {
      for (const item of items) {
        if (item.status !== InvoiceStatus.Paid &&
          item.entityType == InvoiceType.Net &&
          this.isDelayed(item.logicalDate)) {
          return "Annetulle palkkapäivälle tehtävä palkkatietoilmoitus myöhästyy, koska ilmoitus pitää tehdä 5 päivän kuluessa palkkapäivästä. Ota huomioon, että tästä saattaa seurata myöhästymismaksu. Haluatko varmasti merkitä palkan maksetuiksi?";
        }
      }
    }
    return null;
  }

  /** Checks if the Epr report is delayed. */
  private isDelayed(salaryDate?: string): boolean {
    if (this.session.isInRole(SystemRole.Test)) {
      const officialDate = Dates.add((salaryDate ?? "today"),5, "day");
      const expiresAt = Dates.addWorkdays(officialDate, 0);
      const today = Dates.asDate("today");
      return today>expiresAt;
    }
    return false;
  }

  private async generateQrCodeSvgForInvoice(item: InvoiceListItem): Promise<string> {
    const svg = await QrCodes.getValue(
     BicHelper.getBicForIban(item.otherPartyInfo.ibanNumber.replace(/\s+/g, "")),
     item.otherPartyInfo.avatar.displayName,
     item.otherPartyInfo?.ibanNumber,
     item.reference,
     null,
     item.payment,
     item.endAt
   );
   return svg;
 }
}
